import {
  type CollectionReference,
  type DocumentData,
  type QueryDocumentSnapshot,
  collection,
  limit,
  orderBy,
  query,
} from 'firebase/firestore'

import { getFirebaseFirestore } from '@/lib/firebase/app'
import { callable } from '@/lib/firebase/callable'
import { type Subscription, subscribeCollection } from '@/lib/firebase/subscribe'
import type {
  KnowledgeFolder,
  KnowledgeItemRef,
  KnowledgePage,
  KnowledgeProcessingStatus,
  KnowledgeProvenance,
  KnowledgeRevision,
  WebsiteReadBatch,
  WebsiteReadPage,
  WebsiteReadRun,
} from '@/types/knowledge'

export interface KnowledgeScope {
  workspaceId: string
  brandId: string
}

function brandCollection(scope: KnowledgeScope, name: string): CollectionReference<DocumentData> {
  return collection(
    getFirebaseFirestore(),
    'workspaces',
    scope.workspaceId,
    'brands',
    scope.brandId,
    name,
  )
}

const PROCESSING_STATUSES: readonly KnowledgeProcessingStatus[] = [
  'pending',
  'queued',
  'processing',
  'done',
  'failed',
  'rejected',
]

function asProcessingStatus(value: unknown): KnowledgeProcessingStatus {
  return typeof value === 'string' && (PROCESSING_STATUSES as readonly string[]).includes(value)
    ? (value as KnowledgeProcessingStatus)
    : 'pending'
}

function asIsoOrNull(value: unknown): string | null {
  return typeof value === 'string' && value !== '' ? value : null
}

function asStrings(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === 'string')
    : []
}

function toProvenance(value: unknown): KnowledgeProvenance | undefined {
  if (typeof value !== 'object' || value === null) return undefined
  const record = value as DocumentData
  if (typeof record.url !== 'string' || record.url === '') return undefined
  return {
    url: record.url,
    ...(typeof record.title === 'string' && record.title ? { title: record.title } : {}),
    fetchedAt: asIsoOrNull(record.fetchedAt),
  }
}

function toKnowledgePage(snapshot: QueryDocumentSnapshot<DocumentData>): KnowledgePage {
  const data = snapshot.data()
  return {
    slug: snapshot.id,
    folderId: typeof data.folderId === 'string' && data.folderId ? data.folderId : null,
    title: String(data.name ?? ''),
    ...(typeof data.summary === 'string' && data.summary ? { summary: data.summary } : {}),
    tags: asStrings(data.tags),
    body: typeof data.body === 'string' ? data.body : '',
    distilledFrom: Array.isArray(data.distilledFrom)
      ? data.distilledFrom
          .map(toProvenance)
          .filter((entry): entry is KnowledgeProvenance => entry !== undefined)
      : [],
    ...(typeof data.currentRevision === 'string' ? { currentRevision: data.currentRevision } : {}),
    ...(data.lastAuthor === 'raldo' || data.lastAuthor === 'user'
      ? { lastAuthor: data.lastAuthor }
      : {}),
    processingStatus: asProcessingStatus(data.processingStatus),
    updatedAt: asIsoOrNull(data.updatedAt),
  }
}

/** Live view of the brand's knowledge library, newest first; `authored` is filtered in the mapper to avoid a composite index. */
export function subscribeKnowledgePages(scope: KnowledgeScope): Subscription<KnowledgePage[]> {
  return subscribeCollection(
    query(brandCollection(scope, 'knowledgeBase'), orderBy('updatedAt', 'desc')),
    (snapshot) =>
      snapshot.docs
        .filter((doc) => doc.data().source === 'authored')
        .map((doc) => toKnowledgePage(doc)),
  )
}

function toKnowledgeFolder(snapshot: QueryDocumentSnapshot<DocumentData>): KnowledgeFolder {
  const data = snapshot.data()
  return {
    id: snapshot.id,
    name: String(data.name ?? ''),
    parentId: typeof data.parentId === 'string' && data.parentId ? data.parentId : null,
    createdAt: asIsoOrNull(data.createdAt),
    updatedAt: asIsoOrNull(data.updatedAt),
  }
}

/** Live view of the brand's knowledge folders, sorted by locale here because Firestore orders by code point. */
export function subscribeKnowledgeFolders(scope: KnowledgeScope): Subscription<KnowledgeFolder[]> {
  return subscribeCollection(brandCollection(scope, 'knowledgeFolders'), (snapshot) =>
    snapshot.docs.map(toKnowledgeFolder).sort((left, right) => left.name.localeCompare(right.name)),
  )
}

export interface CreateKnowledgeFolderInput {
  name: string
  parentId?: string | null
}

export interface KnowledgeFolderResult {
  folderId: string
}

export const createKnowledgeFolder = callable<CreateKnowledgeFolderInput, KnowledgeFolderResult>(
  'createKnowledgeFolder',
)

export interface RenameKnowledgeFolderInput {
  folderId: string
  name: string
}

export const renameKnowledgeFolder = callable<RenameKnowledgeFolderInput, KnowledgeFolderResult>(
  'renameKnowledgeFolder',
)

export interface DeleteKnowledgeFolderInput {
  folderId: string
}

export const deleteKnowledgeFolder = callable<DeleteKnowledgeFolderInput, void>(
  'deleteKnowledgeFolder',
)

export interface MoveKnowledgeItemInput extends KnowledgeItemRef {
  targetFolderId: string | null
}

export const moveKnowledgeItem = callable<MoveKnowledgeItemInput, void>('moveKnowledgeItem')

function toWebsiteReadBatch(value: unknown): WebsiteReadBatch | undefined {
  if (typeof value !== 'object' || value === null) return undefined
  const record = value as DocumentData
  if (typeof record.batchId !== 'string') return undefined
  return {
    batchId: record.batchId,
    urlCount: typeof record.urlCount === 'number' ? record.urlCount : 0,
    step: record.step as WebsiteReadBatch['step'],
  }
}

function toWebsiteReadPage(value: unknown): WebsiteReadPage | undefined {
  if (typeof value !== 'object' || value === null) return undefined
  const record = value as DocumentData
  if (typeof record.bucket !== 'string') return undefined
  return {
    bucket: record.bucket as WebsiteReadPage['bucket'],
    step: record.step as WebsiteReadPage['step'],
    ...(typeof record.slug === 'string' ? { slug: record.slug } : {}),
    ...(typeof record.sourceCount === 'number' ? { sourceCount: record.sourceCount } : {}),
  }
}

function toWebsiteReadRun(snapshot: QueryDocumentSnapshot<DocumentData>): WebsiteReadRun {
  const data = snapshot.data()
  return {
    id: snapshot.id,
    websiteUrl: String(data.websiteUrl ?? ''),
    status: data.status as WebsiteReadRun['status'],
    discoveredCount: typeof data.discoveredCount === 'number' ? data.discoveredCount : 0,
    selectedCount: typeof data.selectedCount === 'number' ? data.selectedCount : 0,
    batches: Array.isArray(data.batches)
      ? data.batches
          .map(toWebsiteReadBatch)
          .filter((batch): batch is WebsiteReadBatch => batch !== undefined)
      : [],
    pages: Array.isArray(data.pages)
      ? data.pages
          .map(toWebsiteReadPage)
          .filter((page): page is WebsiteReadPage => page !== undefined)
      : [],
    ...(typeof data.error === 'string' ? { error: data.error } : {}),
    createdAt: asIsoOrNull(data.createdAt),
  }
}

export function subscribeLatestWebsiteRead(
  scope: KnowledgeScope,
): Subscription<WebsiteReadRun | null> {
  return subscribeCollection(
    query(brandCollection(scope, 'websiteReadRuns'), orderBy('createdAt', 'desc'), limit(1)),
    (snapshot) => {
      const [latest] = snapshot.docs
      return latest ? toWebsiteReadRun(latest) : null
    },
  )
}

export interface WriteKnowledgePageInput {
  slug?: string
  title?: string
  body: string
  summary?: string
  tags?: string[]
  folderId?: string | null
}

export interface WriteKnowledgePageResult {
  slug: string
  title: string
  revisionId: string
  created: boolean
}

export const writeKnowledgePage = callable<WriteKnowledgePageInput, WriteKnowledgePageResult>(
  'writeKnowledgePage',
)

function toKnowledgeRevision(snapshot: QueryDocumentSnapshot<DocumentData>): KnowledgeRevision {
  const data = snapshot.data()
  return {
    id: snapshot.id,
    parentId: typeof data.parentId === 'string' ? data.parentId : null,
    title: String(data.title ?? ''),
    body: typeof data.body === 'string' ? data.body : '',
    author: data.author === 'user' ? 'user' : 'raldo',
    reason:
      data.reason === 'edited' || data.reason === 'rewritten' || data.reason === 'restored'
        ? data.reason
        : 'generated',
    ...(typeof data.instruction === 'string' && data.instruction
      ? { instruction: data.instruction }
      : {}),
    createdAt: asIsoOrNull(data.createdAt),
  }
}

export function subscribeKnowledgePageRevisions(
  scope: KnowledgeScope,
  slug: string,
): Subscription<KnowledgeRevision[]> {
  return subscribeCollection(
    query(
      collection(
        getFirebaseFirestore(),
        'workspaces',
        scope.workspaceId,
        'brands',
        scope.brandId,
        'knowledgeBase',
        slug,
        'revisions',
      ),
      orderBy('createdAt', 'asc'),
    ),
    (snapshot) => snapshot.docs.map(toKnowledgeRevision),
  )
}

export interface RestoreKnowledgePageRevisionInput {
  slug: string
  revisionId: string
}

export interface RestoreKnowledgePageRevisionResult {
  revisionId: string
}

export const restoreKnowledgePageRevision = callable<
  RestoreKnowledgePageRevisionInput,
  RestoreKnowledgePageRevisionResult
>('restoreKnowledgePageRevision')

export interface DeleteKnowledgeSourceInput {
  itemId: string
}

export const deleteKnowledgePage = callable<DeleteKnowledgeSourceInput, void>(
  'deleteKnowledgeSource',
)
